import type { AlineFontName, PDFSerializer } from '@aline/common';
import { Queries, urlToBase64Asset } from '@aline/common';
import { keys, pick } from 'ramda';
import type { Editor } from 'slate';

import aptosBoldItalic from './fonts/aptos/Aptos-Bold-Italic.ttf';
import aptosBold from './fonts/aptos/Aptos-Bold.ttf';
import aptosDisplayBoldItalic from './fonts/aptos/Aptos-Display-Bold-Italic.ttf';
import aptosDisplayBold from './fonts/aptos/Aptos-Display-Bold.ttf';
import aptosDisplayItalic from './fonts/aptos/Aptos-Display-Italic.ttf';
import aptosDisplayRegular from './fonts/aptos/Aptos-Display.ttf';
import aptosItalic from './fonts/aptos/Aptos-Italic.ttf';
import aptosMonoBoldItalic from './fonts/aptos/Aptos-Mono-Bold-Italic.ttf';
import aptosMonoBold from './fonts/aptos/Aptos-Mono-Bold.ttf';
import aptosMonoItalic from './fonts/aptos/Aptos-Mono-Italic.ttf';
import aptosMonoRegular from './fonts/aptos/Aptos-Mono.ttf';
import aptosNarrowBoldItalic from './fonts/aptos/Aptos-Narrow-Bold-Italic.ttf';
import aptosNarrowBold from './fonts/aptos/Aptos-Narrow-Bold.ttf';
import aptosNarrowItalic from './fonts/aptos/Aptos-Narrow-Italic.ttf';
import aptosNarrowRegular from './fonts/aptos/Aptos-Narrow.ttf';
import aptosSerifBoldItalic from './fonts/aptos/Aptos-Serif-Bold-Italic.ttf';
import aptosSerifBold from './fonts/aptos/Aptos-Serif-Bold.ttf';
import aptosSerifItalic from './fonts/aptos/Aptos-Serif-Italic.ttf';
import aptosSerifRegular from './fonts/aptos/Aptos-Serif.ttf';
import aptosRegular from './fonts/aptos/Aptos.ttf';
import arialRegular from './fonts/arial/Arial.ttf';
import arialBold from './fonts/arial/ArialBold.ttf';
import arialBoldItalic from './fonts/arial/ArialBoldItalic.ttf';
import arialItalic from './fonts/arial/ArialItalic.ttf';
import calibriRegular from './fonts/calibri/Calibri.ttf';
import calibriBold from './fonts/calibri/CalibriBold.ttf';
import calibriBoldItalic from './fonts/calibri/CalibriBoldItalic.ttf';
import calibriItalic from './fonts/calibri/CalibriItalic.ttf';
import cambriaRegular from './fonts/cambria/Cambria.ttf';
import cambriaBold from './fonts/cambria/CambriaBold.ttf';
import cambriaBoldItalic from './fonts/cambria/CambriaBoldItalic.ttf';
import cambriaItalic from './fonts/cambria/CambriaItalic.ttf';
import centuryGothicRegular from './fonts/century-gothic/CenturyGothic.ttf';
import centuryGothicBold from './fonts/century-gothic/CenturyGothicBold.ttf';
import centuryGothicBoldItalic from './fonts/century-gothic/CenturyGothicBoldItalic.ttf';
import centuryGothicItalic from './fonts/century-gothic/CenturyGothicItalic.ttf';
import courierNewBold from './fonts/courier-new/CourierNewBold.ttf';
import courierNewBoldItalic from './fonts/courier-new/CourierNewBoldItalic.ttf';
import courierNewItalic from './fonts/courier-new/CourierNewItalic.ttf';
import courierNewRegular from './fonts/courier-new/CourierNewRegular.ttf';
import franklinGothicMediumRegular from './fonts/franklin-gothic/FranklinGothicMedium.ttf';
import franklinGothicMediumItalic from './fonts/franklin-gothic/FranklinGothicMediumItalic.ttf';
import freeSerif from './fonts/free-serif/FreeSerif.ttf';
import garamondBold from './fonts/garamond/GaramondBold.ttf';
import garamondBoldItalic from './fonts/garamond/GaramondBoldItalic.ttf';
import garamondItalic from './fonts/garamond/GaramondItalic.ttf';
import garamondRegular from './fonts/garamond/GaramondRegular.ttf';
import georgiaBold from './fonts/georgia/GeorgiaBold.ttf';
import georgiaBoldItalic from './fonts/georgia/GeorgiaBoldItalic.ttf';
import georgiaItalic from './fonts/georgia/GeorgiaItalic.ttf';
import georgiaRegular from './fonts/georgia/GeorgiaRegular.ttf';
import helveticaBold from './fonts/helvetica/HelveticaBold.ttf';
import helveticaBoldItalic from './fonts/helvetica/HelveticaBoldItalic.ttf';
import helveticaItalic from './fonts/helvetica/HelveticaItalic.ttf';
import helveticaRegular from './fonts/helvetica/HelveticaRegular.ttf';
import helveticaNeueBold from './fonts/helvetica-neue/HelveticaNeueBold.ttf';
import helveticaNeueBoldItalic from './fonts/helvetica-neue/HelveticaNeueBoldItalic.ttf';
import helveticaNeueItalic from './fonts/helvetica-neue/HelveticaNeueItalic.ttf';
import helveticaNeueRegular from './fonts/helvetica-neue/HelveticaNeueRegular.ttf';
import lucidaSansTypewriterBold from './fonts/lucida-sans/LucidaSansTypewriterBold.ttf';
import lucidaSansTypewriterBoldItalic from './fonts/lucida-sans/LucidaSansTypewriterBoldItalic.ttf';
import lucidaSansTypewriterItalic from './fonts/lucida-sans/LucidaSansTypewriterItalic.ttf';
import lucidaSansTypewriterRegular from './fonts/lucida-sans/LucidaSansTypewriterRegular.ttf';
import openSansBold from './fonts/open-sans/OpenSansBold.ttf';
import openSansBoldItalic from './fonts/open-sans/OpenSansBoldItalic.ttf';
import openSansItalic from './fonts/open-sans/OpenSansItalic.ttf';
import openSansRegular from './fonts/open-sans/OpenSansRegular.ttf';
import palatinoBold from './fonts/palatino/PalatinoBold.ttf';
import palatinoBoldItalic from './fonts/palatino/PalatinoBoldItalic.ttf';
import palatinoItalic from './fonts/palatino/PalatinoItalic.ttf';
import palatinoRegular from './fonts/palatino/PalatinoRegular.ttf';
import symbol from './fonts/symbol/Symbol.ttf';
import tahomaRegular from './fonts/tahoma/Tahoma.ttf';
import tahomaBold from './fonts/tahoma/TahomaBold.ttf';
import tahomaBoldItalic from './fonts/tahoma/TahomaBoldItalic.ttf';
import tahomaItalic from './fonts/tahoma/TahomaItalic.ttf';
import timesNewRomanRegular from './fonts/times-new-roman/TimesNewRoman.ttf';
import timesNewRomanBold from './fonts/times-new-roman/TimesNewRomanBold.ttf';
import timesNewRomanBoldItalic from './fonts/times-new-roman/TimesNewRomanBoldItalic.ttf';
import timesNewRomanItalic from './fonts/times-new-roman/TimesNewRomanItalic.ttf';
import verdanaRegular from './fonts/verdana/Verdana.ttf';
import verdanaBold from './fonts/verdana/VerdanaBold.ttf';
import verdanaBoldItalic from './fonts/verdana/VerdanaBoldItalic.ttf';
import verdanaItalic from './fonts/verdana/VerdanaItalic.ttf';

interface ClientFontItem {
  bold: string;
  boldItalic: string;
  italic: string;
  regular: string;
}
type ClientFontMap = Record<AlineFontName, ClientFontItem>;

export const alineClientFontMap: ClientFontMap = {
  Aptos: {
    bold: aptosBold,
    boldItalic: aptosBoldItalic,
    italic: aptosItalic,
    regular: aptosRegular,
  },
  'Aptos Display': {
    bold: aptosDisplayBold,
    boldItalic: aptosDisplayBoldItalic,
    italic: aptosDisplayItalic,
    regular: aptosDisplayRegular,
  },
  'Aptos Mono': {
    bold: aptosMonoBold,
    boldItalic: aptosMonoBoldItalic,
    italic: aptosMonoItalic,
    regular: aptosMonoRegular,
  },
  'Aptos Narrow': {
    bold: aptosNarrowBold,
    boldItalic: aptosNarrowBoldItalic,
    italic: aptosNarrowItalic,
    regular: aptosNarrowRegular,
  },
  'Aptos Serif': {
    bold: aptosSerifBold,
    boldItalic: aptosSerifBoldItalic,
    italic: aptosSerifItalic,
    regular: aptosSerifRegular,
  },
  Arial: {
    bold: arialBold,
    boldItalic: arialBoldItalic,
    italic: arialItalic,
    regular: arialRegular,
  },
  Calibri: {
    bold: calibriBold,
    boldItalic: calibriBoldItalic,
    italic: calibriItalic,
    regular: calibriRegular,
  },
  Cambria: {
    bold: cambriaBold,
    boldItalic: cambriaBoldItalic,
    italic: cambriaItalic,
    regular: cambriaRegular,
  },
  'Century Gothic': {
    bold: centuryGothicBold,
    boldItalic: centuryGothicBoldItalic,
    italic: centuryGothicItalic,
    regular: centuryGothicRegular,
  },
  'Courier New': {
    bold: courierNewBold,
    boldItalic: courierNewBoldItalic,
    italic: courierNewItalic,
    regular: courierNewRegular,
  },
  // Note: Franklin Gothic Medium is a font that doesn't have bold so we only use these 2 font weights on purpose.
  'Franklin Gothic Medium': {
    bold: franklinGothicMediumRegular,
    boldItalic: franklinGothicMediumItalic,
    italic: franklinGothicMediumItalic,
    regular: franklinGothicMediumRegular,
  },
  'Free Serif': {
    bold: freeSerif,
    boldItalic: freeSerif,
    italic: freeSerif,
    regular: freeSerif,
  },
  Garamond: {
    bold: garamondBold,
    boldItalic: garamondBoldItalic,
    italic: garamondItalic,
    regular: garamondRegular,
  },
  Georgia: {
    bold: georgiaBold,
    boldItalic: georgiaBoldItalic,
    italic: georgiaItalic,
    regular: georgiaRegular,
  },
  Helvetica: {
    bold: helveticaBold,
    boldItalic: helveticaBoldItalic,
    italic: helveticaItalic,
    regular: helveticaRegular,
  },
  'Helvetica Neue': {
    bold: helveticaNeueBold,
    boldItalic: helveticaNeueBoldItalic,
    italic: helveticaNeueItalic,
    regular: helveticaNeueRegular,
  },
  'Lucida Sans Typewriter': {
    bold: lucidaSansTypewriterBold,
    boldItalic: lucidaSansTypewriterBoldItalic,
    italic: lucidaSansTypewriterItalic,
    regular: lucidaSansTypewriterRegular,
  },
  'Open Sans': {
    bold: openSansBold,
    boldItalic: openSansBoldItalic,
    italic: openSansItalic,
    regular: openSansRegular,
  },
  Palatino: {
    bold: palatinoBold,
    boldItalic: palatinoBoldItalic,
    italic: palatinoItalic,
    regular: palatinoRegular,
  },
  Symbol: {
    bold: symbol,
    boldItalic: symbol,
    italic: symbol,
    regular: symbol,
  },
  Tahoma: {
    bold: tahomaBold,
    boldItalic: tahomaBoldItalic,
    italic: tahomaItalic,
    regular: tahomaRegular,
  },
  'Times New Roman': {
    bold: timesNewRomanBold,
    boldItalic: timesNewRomanBoldItalic,
    italic: timesNewRomanItalic,
    regular: timesNewRomanRegular,
  },
  Verdana: {
    bold: verdanaBold,
    boldItalic: verdanaBoldItalic,
    italic: verdanaItalic,
    regular: verdanaRegular,
  },
};

export async function loadFont(url: string, descriptors: FontFaceDescriptors): Promise<FontFace> {
  const font = new FontFace(descriptors.family, `url(${url})`, descriptors);
  const fontFace = await font.load();
  document.fonts.add(font);
  return fontFace;
}

export async function loadAllFonts(): Promise<FontFace[]> {
  const fontKeys = keys(alineClientFontMap);

  const fontFaceListNested = await Promise.all(
    fontKeys.map(async fontName => {
      const item = alineClientFontMap[fontName];
      return Promise.all(
        (keys(item) as Array<keyof ClientFontItem>).map(fontStyle => {
          return loadFont(item[fontStyle], getFontDescriptors(fontName, fontStyle));
        })
      );
    })
  );
  return fontFaceListNested.flat();
}

export function getFontDescriptors(
  alineFontName: AlineFontName,
  key: keyof ClientFontItem
): FontFaceDescriptors {
  switch (key) {
    case 'bold':
      return { family: alineFontName, style: 'normal', weight: 'bold' };
    case 'boldItalic':
      return { family: alineFontName, style: 'italic', weight: 'bold' };
    case 'italic':
      return { family: alineFontName, style: 'italic', weight: 'normal' };
    case 'regular':
      return { family: alineFontName, style: 'normal', weight: 'normal' };
  }
}

export async function buildClientFontMap(editor: Editor): Promise<PDFSerializer.FontMap> {
  const uniqueFontTypes = Queries.getUniqueFontTypes(editor);

  const partialFontMap = pick(
    [...uniqueFontTypes, 'Arial', 'Free Serif'] as AlineFontName[],
    alineClientFontMap
  );

  const fontNames = keys(partialFontMap) as AlineFontName[];
  const fontMap: PDFSerializer.FontMap = new Map();

  await Promise.all(
    fontNames.map(async fontName => {
      const fontItem = partialFontMap[fontName];

      const [bold, boldItalic, italic, regular] = await Promise.all([
        urlToBase64Asset(fontItem.bold),
        urlToBase64Asset(fontItem.boldItalic),
        urlToBase64Asset(fontItem.italic),
        urlToBase64Asset(fontItem.regular),
      ]);

      fontMap.set(fontName, { bold, boldItalic, italic, regular });
    })
  );

  return fontMap;
}
